/**
 * Wrapper for a DOM element to provide inline editing functionality.
 */
export default class EditableElement {

    /** @type {HTMLElement} */
    node

    /**
     * @callback submitHandler
     * @param {string} content New content to be submitted
     * @param {EditableElement} editableElement
     * @returns {boolean|string} Boolean whether editing should be finished or a string to replace the current content
     */

    /** @type {submitHandler} */
    onSubmit

    /** @type {HTMLElement} */
    text = document.createElement('span')

    /** @type {string} */
    originalValue

    /**
     * Create a new editable element around a DOM element.
     * @param {HTMLElement} node DOM element to wrap with editing functionality
     * @param {submitHandler} onSubmit Submit handler called with the new content
     */
    constructor(node, onSubmit) {
        this.node = node
        this.onSubmit = onSubmit
        this.init()
    }

    init() {
        this.node.classList.add('editable')

        const controls = document.createElement('div')
        controls.classList.add('editable-controls')
        const editBtn = document.createElement('button')
        editBtn.setAttribute('class', 'editable-btn edit')
        editBtn.innerHTML = '<i class="i-edit i-sm"></i>'
        editBtn.addEventListener('click', this.edit.bind(this))
        controls.append(editBtn)

        const submitBtn = document.createElement('button')
        submitBtn.setAttribute('class', 'editable-btn submit')
        submitBtn.innerHTML = '<i class="i-check i-sm"></i>'
        submitBtn.type = 'submit'
        submitBtn.addEventListener('click', this.submit.bind(this))
        controls.append(submitBtn)

        const cancelBtn = document.createElement('button')
        cancelBtn.setAttribute('class', 'editable-btn cancel')
        cancelBtn.innerHTML = '<i class="i-close i-sm"></i>'
        cancelBtn.addEventListener('click', this.cancel.bind(this))
        controls.append(cancelBtn)

        this.text.classList.add('editable-text')
        this.text.textContent = this.node.textContent.trim()
        if (!this.text.textContent) this.node.classList.add('empty')
        this.text.addEventListener('click', this.edit.bind(this))
        this.text.addEventListener('keydown', e => {
            if (e.code === 'Enter') {
                e.preventDefault()
                this.submit()
            } else if (e.code === 'Escape') {
                e.preventDefault()
                this.cancel()
            } else {
                this.node.classList.remove('empty')
            }
        })
        this.text.addEventListener('keyup', e => {
            if (!this.text.textContent.length) {
                this.node.classList.add('empty')
            }
        })

        this.node.textContent = ''

        if (this.node.dataset.placeholder) {
            const placeholder = document.createElement('span')
            placeholder.classList.add('editable-placeholder')
            placeholder.textContent = this.node.dataset.placeholder
            placeholder.addEventListener('click', this.edit.bind(this))
            this.node.append(placeholder)
        }

        this.node.append(this.text)
        this.node.append(controls)
    }

    edit() {
        this.node.classList.add('editing')
        this.originalValue = this.text.textContent
        this.text.contentEditable = 'true'
        this.text.focus()
    }

    cancel() {
        this.node.classList.remove('editing')
        this.setValue(this.originalValue)
        this.text.contentEditable = 'false'
    }

    async submit() {
        const result = await this.onSubmit(this.text.textContent, this)
        if (result !== false) {
            if (typeof result === 'string') {
                this.setValue(result)
            }
            this.node.classList.remove('editing')
            this.text.contentEditable = 'false'
        }
    }

    setValue(value) {
        this.text.textContent = value
        this.node.classList.toggle('empty', !this.text.textContent)
    }

}
